import { useState } from "react";
import {
    Box,
    Container,
    IconButton,
    Link,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useWorkspace } from "../store/WorkspaceContext";
import AboutDialog from "./AboutDialog";

export default function AppLayout({ children }) {
    const { settings, updateSettings } = useWorkspace();
    const [aboutOpen, setAboutOpen] = useState(false);
    const darkMode = settings.theme === "dark";

    const refreshPage = () => {
        window.location.reload();
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
            }}>
            <AppBar
                position="sticky"
                elevation={0}
                color="default"
                sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Toolbar sx={{ gap: 2, userSelect: "none" }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        role="button"
                        tabIndex={0}
                        aria-label="Refresh page"
                        onClick={refreshPage}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                refreshPage();
                            }
                        }}
                        sx={{
                            alignItems: "center",
                            cursor: "pointer",
                            minWidth: 0,
                        }}>
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/logo.png`}
                            alt="TextLab logo"
                            sx={{
                                width: 30,
                                height: 30,
                                objectFit: "contain",
                                display: "block",
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            variant="h6"
                            fontWeight={800}
                            noWrap
                            sx={{ color: "primary.main" }}>
                            TextLab
                        </Typography>
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="About TextLab">
                        <IconButton
                            aria-label="About TextLab"
                            onClick={() => setAboutOpen(true)}>
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={darkMode ? "Light mode" : "Dark mode"}>
                        <IconButton
                            aria-label="Toggle theme"
                            onClick={() =>
                                updateSettings({
                                    theme: darkMode ? "light" : "dark",
                                })
                            }>
                            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Box component="main" sx={{ flexGrow: 1 }}>
                {children}
            </Box>

            <Box
                component="footer"
                sx={{
                    borderTop: 1,
                    borderColor: "divider",
                    py: 2,
                    mt: 4,
                }}>
                <Container maxWidth="xl">
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", userSelect: "none" }}>
                        TextLab © 2026 — Built by{" "}
                        <Link
                            href="https://bilalabdulhadi.com"
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{ color: "primary.main", fontWeight: 800 }}>
                            Bilal Abdulhadi
                        </Link>
                        {" · "}
                        <Link
                            href="https://github.com/bilalabdulhadii/react-text-lab"
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{ color: "primary.main", fontWeight: 700 }}>
                            GitHub
                        </Link>
                    </Typography>
                </Container>
            </Box>

            <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
        </Box>
    );
}
